/**
 * Utility functions for handling pricing based on user groups
 */

export interface User {
  id: number;
  username: string;
  name: string;
  surname: string;
  company: string;
  erp_id: number;
  country: string;
  city: string;
  email: string;
  password: string;
  phone: string;
  address: string;
  is_active: boolean;
  is_admin: boolean;
  is_client: boolean;
  activate_grid_view: boolean;
  zone: string;
  group?: string;
  margin_group_id?: number | null;
  margin_group?: {
    id: number;
    name: string;
    margin_percentage: number;
    is_active: boolean;
  } | null;
  is_super_admin?: boolean;
  allowed_categories?: string[];
}

export interface ArticlePrice {
  CMSH: number;
  CMSH1: number;
  CMSH2: number;
  CMSH3: number;
  CMSH4: number;
  CMSH5: number;
  CMSH6: number;
  CMSH7: number;
  CMSH8: number;
  CMSH9: number;
  CMSH10: number;
  CMSH11: number | null;
  CMSH12: number;
  CMSH13: number;
  CMSH14: number;
  CMSH15: number;
  CMSH16: number;
  CMSH17: number;
  CMSH18: number;
  CMSH19: number;
}

/**
 * Get the current authenticated user from localStorage
 */
export const getCurrentUser = (): User | null => {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    return null;
  }
};

/**
 * Get the user's group for pricing purposes
 */
export const getUserGroup = (user: User | null): string => {
  return user?.group || 'A';
};

/**
 * Get the price field name based on user group
 * Group A -> CMSH (base price)
 * Group B -> CMSH1
 * Group C -> CMSH2
 * Group D -> CMSH3
 * etc.
 */
export const getPriceFieldByGroup = (group: string): keyof ArticlePrice => {
  const groupMap: Record<string, keyof ArticlePrice> = {
    'A': 'CMSH',
    'B': 'CMSH1',
    'C': 'CMSH2',
    'D': 'CMSH3',
    'E': 'CMSH4',
    'F': 'CMSH5',
    'G': 'CMSH6',
    'H': 'CMSH7',
    'I': 'CMSH8',
    'J': 'CMSH9',
    'K': 'CMSH10',
    'L': 'CMSH11',
    'M': 'CMSH12',
    'N': 'CMSH13',
    'O': 'CMSH14',
    'P': 'CMSH15',
    'Q': 'CMSH16',
    'R': 'CMSH17',
    'S': 'CMSH18',
    'T': 'CMSH19'
  };
  
  return groupMap[group.toUpperCase()] || 'CMSH';
};

/**
 * Get the retail price field name based on user group
 * For all users, retail price is always CMSH4
 */
export const getRetailPriceFieldByGroup = (group: string): keyof ArticlePrice => {
  return 'CMSH4';
};

/**
 * Apply client margin group percentage on top of a base group/retail price.
 * final_price = group_price × (1 + margin_percentage / 100)
 */
export const applyUserMargin = (price: number, user: User | null = getCurrentUser()): number => {
  if (!price || price <= 0) {
    return 0;
  }
  const marginPct = Number(user?.margin_group?.margin_percentage ?? 0);
  if (!marginPct || marginPct <= 0) {
    return price;
  }
  return price * (1 + marginPct / 100);
};

/**
 * Get the appropriate price for the current user based on their group
 */
export const getUserPrice = (article: ArticlePrice): number => {
  const user = getCurrentUser();
  const userGroup = getUserGroup(user);
  const priceField = getPriceFieldByGroup(userGroup);
  const price = article[priceField];
  
  // Return the price if it exists and is greater than 0, otherwise fallback to CMSH
  const groupPrice = price && price > 0 ? price : (article.CMSH || 0);
  return applyUserMargin(groupPrice, user);
};

/**
 * Get the retail price for the current user based on their group
 * For all users, retail price is always CMSH4
 */
export const getUserRetailPrice = (article: ArticlePrice): number => {
  const user = getCurrentUser();
  const price = article.CMSH4;
  
  // Return the price if it exists and is greater than 0, otherwise fallback to CMSH
  const retailPrice = price && price > 0 ? price : (article.CMSH || 0);
  return applyUserMargin(retailPrice, user);
};

/**
 * Get the display name for a group
 */
export const getGroupDisplayName = (group: string): string => {
  const displayNames: Record<string, string> = {
    'A': 'Standard',
    'B': 'Silver',
    'C': 'Gold',
    'D': 'Platinum',
    'E': 'Premium',
    'F': 'VIP',
    'G': 'Elite',
    'H': 'Diamond',
    'I': 'Ruby',
    'J': 'Sapphire'
  };
  
  return displayNames[group.toUpperCase()] || 'Standard';
};

/**
 * Platform currency is always EUR.
 */
export const getCurrencyByGroup = (_group?: string): string => {
  return 'EUR';
};

/**
 * Get the currency symbol (always €)
 */
export const getCurrencySymbolByGroup = (_group?: string): string => {
  return '€';
};

/**
 * Get the currency for the current user (always EUR)
 */
export const getCurrentUserCurrency = (): string => {
  return 'EUR';
};

/**
 * Get the currency symbol for the current user (always €)
 */
export const getCurrentUserCurrencySymbol = (): string => {
  return '€';
};

/**
 * Format price with EUR
 */
export const formatPriceWithCurrency = (price: number | string | undefined | null, _group?: string): string => {
  let numericPrice = 0;

  if (price === undefined || price === null) {
    numericPrice = 0;
  } else if (typeof price === 'string') {
    numericPrice = parseFloat(price) || 0;
  } else if (typeof price === 'number') {
    numericPrice = price;
  } else {
    numericPrice = 0;
  }

  if (isNaN(numericPrice)) {
    numericPrice = 0;
  }

  return `${numericPrice.toFixed(2)} €`;
}; 